import { AutoCompleteProps } from "@/common/types";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "../ui/button";
import { useEffect, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

const AutoComplete = ({
  selectedValue,
  placeHolder,
  options,
  labelKey,
  onSelect,
  onChange,
}: AutoCompleteProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string>("");
  const [optionsList, setOptionsList] = useState<any[]>(options);
  const [selectedOption, setSelectedOption] = useState<any | null>(null);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    setOptionsList(options);
  }, [options]);

  useEffect(() => {
    onChange?.(search);
  }, [search]);

  useEffect(() => {
    onSelect(selectedOption);
  }, [selectedOption]);

  useEffect(() => {
    if (selectedValue) {
      setSelectedOption(
        options.find((option) => option?.id === selectedValue) || null
      );
    } else {
      setSelectedOption(null);
      setValue("");
    }
  }, [selectedValue]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {value
            ? options.find((option) => option[labelKey] === value)?.[labelKey]
            : `Select ${placeHolder || ""}...`}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full">
        <Command>
          <CommandInput
            placeholder={`Search ${placeHolder || ""}...`}
            className="outline-none pb-2"
            onValueChange={(value) => {
              setSearch(value);
            }}
          />
          <Separator className="w-full border-b" />
          <CommandList>
            <CommandEmpty>{`No ${
              placeHolder || "result"
            }s found.`}</CommandEmpty>
            <CommandGroup>
              {optionsList.map((option) => (
                <CommandItem
                  key={option[labelKey]}
                  value={option[labelKey]}
                  onSelect={(currentValue) => {
                    const selectedValue =
                      currentValue === value ? "" : currentValue;
                    setValue(selectedValue);
                    setOpen(false);
                    setSelectedOption(
                      options.find(
                        (option) => option[labelKey] === selectedValue
                      ) || null
                    );
                  }}
                  className="cursor-pointer flex items-center py-2 text-sm"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option[labelKey] ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option[labelKey]}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AutoComplete;
